import { FC } from "react";

import { Box } from "@mui/material";
import Grid from "@mui/material/Grid";

import { RecurrenceCardLayout } from "./RecurrenceCardLayout";

import { useTranslate } from "@/common/hooks/useTranslate";
import { DeleteConfirmations } from "@/components/DeleteConfirmation/DeleteConfirmation";
import { DashboardBadge } from "@/dashboard-design-system/Badge";
import { DashboardIconButton } from "@/dashboard-design-system/DashboardButton";
import { DashboardTooltip } from "@/dashboard-design-system/DashboardTooltip/DashboardTooltip";
import { DashboardSkeleton } from "@/dashboard-design-system/Skeleton/DashboardSkeleton";
import { DashboardTypography } from "@/dashboard-design-system/Typography/DashboardTypography";
import { Hide, Reveal } from "@/icons/outlined";

export type RecurrenceCardActions = {
  move?: {
    onMoveUp: () => void;
    onMoveDown: () => void;
    isFirst: boolean;
    isLast: boolean;
  };
  visibility?: {
    isHidden: boolean;
    onToggle: () => void;
    disabled?: boolean;
    disabledTooltip?: string;
  };
  delete?: {
    onDelete: () => void;
    disabled?: boolean;
    confirmBefore?: boolean;
    disabledTooltip?: string;
  };
};

export interface RecurrenceCardProps {
  id: string;
  header: {
    title: string;
    isHidden?: boolean;
    titleVariant?: "subtitle1" | "body2";
  };
  actions: RecurrenceCardActions;
  actionsLoading?: boolean;
  children: React.ReactNode;
}

const ACTIONS_SKELETON_SIZE = 32;

export const RecurrenceCard: FC<RecurrenceCardProps> = ({ id, header, actions, actionsLoading = false, children }) => {
  const { t } = useTranslate();

  const headerSlot = (
    <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
      <DashboardTypography variant={header.titleVariant ?? "subtitle1"} gutterBottom sx={{ marginBottom: 0 }}>
        {header.title}
      </DashboardTypography>
      {header.isHidden && (
        <Box
          display="flex"
          alignItems="center"
          gap={1}
          sx={(theme) => ({
            paddingRight: theme.spacing(1),
            "& .MuiBadge-dot": {
              backgroundColor: theme.palette.text.neutral.supershy,
              width: 4,
              height: 4,
              minWidth: 4,
              minHeight: 4,
            },
          })}
        >
          <DashboardBadge variant="dot" />
          <DashboardTypography variant="subtitle1" component="span" sx={{ opacity: 0.4 }}>
            {t("dashboard", "ticketing.recurrence.hidden")}
          </DashboardTypography>
        </Box>
      )}
    </Box>
  );

  const deleteButton =
    actions.delete !== undefined ? (
      actions.delete.disabled && actions.delete.disabledTooltip ? (
        <DashboardTooltip title={actions.delete.disabledTooltip}>
          <span>
            <DeleteConfirmations
              confirmation={actions.delete.onDelete}
              shouldConfirm={actions.delete.confirmBefore ?? true}
              disabled={actions.delete.disabled}
            />
          </span>
        </DashboardTooltip>
      ) : (
        <DeleteConfirmations
          confirmation={actions.delete.onDelete}
          shouldConfirm={actions.delete.confirmBefore ?? true}
          disabled={actions.delete.disabled}
        />
      )
    ) : null;

  const visibilityLabel =
    actions.visibility !== undefined
      ? t("dashboard", actions.visibility.isHidden ? "ticketing.recurrence.showDate" : "ticketing.recurrence.hideDate")
      : "";

  const visibilityButton =
    actions.visibility !== undefined ? (
      <DashboardTooltip
        title={
          actions.visibility.disabled && actions.visibility.disabledTooltip
            ? actions.visibility.disabledTooltip
            : visibilityLabel
        }
      >
        <span>
          <DashboardIconButton
            variant="text"
            size="small"
            vibe="neutral"
            onClick={actions.visibility.onToggle}
            disabled={actions.visibility.disabled}
            aria-label={visibilityLabel}
          >
            {actions.visibility.isHidden ? <Reveal /> : <Hide />}
          </DashboardIconButton>
        </span>
      </DashboardTooltip>
    ) : null;

  const actionsSlot = actionsLoading ? (
    <Grid container justifyContent="flex-end" spacing={0.5} wrap="nowrap">
      <Grid item>
        <DashboardSkeleton
          variant="rounded"
          width={ACTIONS_SKELETON_SIZE}
          height={ACTIONS_SKELETON_SIZE}
          sx={{ borderRadius: 0.5 }}
        />
      </Grid>
      <Grid item>
        <DashboardSkeleton
          variant="rounded"
          width={ACTIONS_SKELETON_SIZE}
          height={ACTIONS_SKELETON_SIZE}
          sx={{ borderRadius: 0.5 }}
        />
      </Grid>
    </Grid>
  ) : (
    <Grid container justifyContent="flex-end" spacing={0.5} wrap="nowrap">
      {visibilityButton && <Grid item>{visibilityButton}</Grid>}
      {deleteButton && <Grid item>{deleteButton}</Grid>}
    </Grid>
  );

  return (
    <Grid item xs={12} key={id}>
      <RecurrenceCardLayout
        isTitleFieldLabel={header.titleVariant === "body2"}
        slots={{
          header: headerSlot,
          actions: actionsSlot,
          content: children,
        }}
      />
    </Grid>
  );
};
